// @ts-strict-ignore
import type { AxiosResponse } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { trackEvent } from '@/analytics/trackEvent';
import { DOC_API_ROUTES } from '@/api-routes/doc';
import { axiosInstance } from '@/axios';
import type { RouteParam } from '@/types';

import {
  QUERY_KEY_ALL,
  useRefetchReadDeeplinks,
} from './useFetchReadDeeplinks';

type RequestPayload = {
  deeplink: string;
  status: string;
};

export const useUpdateDeeplinkReadStatus = () => {
  const refetchReadDeeplinks = useRefetchReadDeeplinks();
  const queryClient = useQueryClient();
  const { subCategory } = useParams<RouteParam>();

  return useMutation(
    ({ deeplink, status }: RequestPayload) =>
      axiosInstance.put(DOC_API_ROUTES.deeplinkStatus(deeplink), {
        status: status,
      }),
    {
      onMutate: ({ deeplink: cardDeeplink, status }) => {
        queryClient.setQueryData<ResponseType>(QUERY_KEY_ALL, (prevData) => {
          if (!prevData) {
            return prevData;
          }

          const deeplinkIndex = prevData.data.results?.findIndex(
            (deeplink) => deeplink === cardDeeplink
          );

          if (deeplinkIndex === -1 && status === 'read') {
            return {
              ...prevData,
              data: {
                ...prevData.data,
                results: [...prevData.data.results, cardDeeplink],
              },
            };
          } else if (deeplinkIndex !== -1 && status === 'unread') {
            const updatedResults = [
              ...prevData.data.results.slice(0, deeplinkIndex),
              ...prevData.data.results.slice(deeplinkIndex + 1),
            ];
            return {
              ...prevData,
              data: {
                ...prevData.data,
                results: updatedResults,
              },
            };
          }
        });
      },
      onSettled: async (_, { status }: RequestPayload) => {
        await refetchReadDeeplinks();
        const event = status === 'read' ? 'marked_as_read' : 'marked_as_unread';
        trackEvent(event, {
          tab_name: subCategory,
        });
      },
    }
  );
};

type ResponseType = AxiosResponse<{ results?: string[] }>;
